<template>
  <TabList
    :tabs="tabs"
    :active-tab="activeTab"
    @tab-changed="tabClicked" />

  <div class="stepper">
    <template
      v-for="(step, i) in steps"
      :key="step.id">
      <div
        v-if="currentStepID === i"
        :id="step.id"
        class="step-content">
        <slot
          :name="step.id"
          v-bind="{ step: steps[i] }" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, ComputedRef, Ref, ref } from 'vue';

  import TabList, { Tab } from '@/org-app/components/controls/TabList.vue';

  export interface IStep {
    id: string;
    title: string;
    isCompleted: Ref<boolean>;
  }

  interface IStepperProps {
    steps: IStep[];
  }

  const { steps } = defineProps<IStepperProps>();

  const currentStepID = ref<number>(0);
  const currentStep = computed(() => steps[currentStepID.value]);

  const tabs: ComputedRef<Tab[]> = computed(() => {
    return steps.map((step, i) => {
      const previousStep = i > 0 ? steps[i - 1] : null;
      const disabled = previousStep != null && !previousStep.isCompleted.value;
      const { id, title } = step;
      const formattedTitle = `${i + 1} - ${title}`;
      return {
        id,
        title: formattedTitle,
        disabled,
      };
    });
  });

  const activeTab = computed<Tab>(() => {
    return tabs.value?.[currentStepID.value];
  });

  function goNext() {
    if (currentStepID.value >= steps.length - 1) {
      return;
    }
    const step = steps[currentStepID.value];
    if (step.isCompleted.value) {
      currentStepID.value += 1;
    }
  }

  function goPrevious() {
    if (currentStepID.value <= 0) {
      return;
    }
    currentStepID.value -= 1;
  }

  function tabClicked(tab: Tab) {
    const index = tabs.value.findIndex((x) => x.id === tab.id);
    if (index >= 0) {
      currentStepID.value = index;
    }
  }

  function goToStep(stepId: string) {
    const index = steps.findIndex((x) => x.id === stepId);
    if (index >= 0) {
      currentStepID.value = index;
    }
  }

  defineExpose({
    goNext,
    goPrevious,
    goToStep,
    currentStep,
  });
</script>

<style lang="scss" scoped>
  .stepper {
    display: flex;
    flex-direction: row;
    width: 100%;
    overflow-y: auto;

    .step-content {
      display: flex;
      justify-content: center;
      width: 100%;
    }
  }
</style>
